// "Bring my data from Portfolio Ledger" (Q9): pick a file, see what it adds, then bring it in.
// Uses the app's own dialog. Resolves true when something was imported.
import { esc } from '../lib/dom';
import { supabaseStore } from '../lib/store';
import { commitImport, previewImport, sha256Hex } from '../core/importFlow';
import { describeSummary, ImportError } from '../core/importer';

function pickFile(): Promise<File | null> {
  return new Promise((res) => {
    const i = document.createElement('input');
    i.type = 'file'; i.accept = '.json,application/json';
    i.addEventListener('change', () => res(i.files?.[0] ?? null), { once: true });
    i.addEventListener('cancel', () => res(null), { once: true });
    i.click();
  });
}

export async function openImportDialog(): Promise<boolean> {
  const f = await pickFile();
  if (!f) return false;
  const d = document.getElementById('dlg') as HTMLDialogElement;
  const show = (inner: string) => { d.innerHTML = `<form method="dialog" novalidate><h2>Bring my data from Portfolio Ledger</h2>${inner}</form>`; if (!d.open) d.showModal(); };
  show('<p class="dim" style="margin-top:8px">Reading…</p>');
  let text = '';
  try {
    text = await f.text();
    const p = await previewImport(supabaseStore, text);
    const lines = describeSummary(p.summary);
    const kind = p.parsed.kind === 'datafile' ? 'Data file' : p.parsed.kind === 'dividends' ? 'Dividend update' : 'Backup';
    const notes = [
      p.parsed.kind === 'backup' ? 'Backups have no hourly values. Bring in the data file as well to include them.' : '',
      p.dividends && (p.dividends.dup || p.dividends.notheld) ? `${p.dividends.dup} already recorded, ${p.dividends.notheld} for stocks you didn’t hold on the ex date (left out, as before).` : '',
      p.parsed.rejected.history ? `${p.parsed.rejected.history} daily value${p.parsed.rejected.history === 1 ? '' : 's'} in the file had no valid date or value and were left out, as before.` : '',
      p.parsed.fixedIds ? `${p.parsed.fixedIds} record${p.parsed.fixedIds === 1 ? '' : 's'} had a missing or repeated id and were given one.` : '',
    ].filter(Boolean);
    return await new Promise<boolean>((resolve) => {
      show(`<p class="dim" style="margin-top:8px"><b style="color:var(--ink)">${esc(kind)}: ${esc(f.name)}</b>. Nothing is added twice and nothing is removed; the file itself is kept in your account.</p>
        ${lines.length ? `<ul class="small" style="margin:10px 0 0;padding-left:18px">${lines.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : '<p class="small dim" style="margin-top:10px">There’s nothing new in this file.</p>'}
        ${notes.map((n) => `<p class="small dim" style="margin-top:6px">${esc(n)}</p>`).join('')}
        <p class="err" role="alert"></p>
        <div class="dlg-actions"><button class="btn" value="cancel">Cancel</button><button class="btn primary" type="button" id="impGo">Bring it in</button></div>`);
      d.onclose = () => resolve(false);
      (d.querySelector('#impGo') as HTMLButtonElement).onclick = async (e) => {
        const b = e.currentTarget as HTMLButtonElement; b.disabled = true; b.textContent = 'Bringing it in…';
        try {
          await commitImport(supabaseStore, text, f.name, await sha256Hex(text));
          d.onclose = null; d.close(); resolve(true);
        } catch (err) {
          (d.querySelector('.err') as HTMLElement).textContent = (err as Error).message;
          b.disabled = false; b.textContent = 'Bring it in';
        }
      };
    });
  } catch (err) {
    show(`<p class="err" style="margin-top:10px">${esc(err instanceof ImportError ? err.message : 'Couldn’t read that file: ' + (err as Error).message)}</p><div class="dlg-actions"><button class="btn">Close</button></div>`);
    return false;
  }
}
